import { InventoryProjectionStatus } from "@redotech/crdb-enums/inventory/inventory-projection-status";
import { useRequiredContext } from "@redotech/react-util/context";
import { inventoryProjectionStatusLabels } from "@redotech/redo-fulfillment-operations-common/inventory-projection-status-labels";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  RedoBadge,
  RedoBadgeProps,
  RedoBadgeSize,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import { getInboundEta, InboundEta } from "./inventory-projection-eta";

function getBadgeProps(
  status: InventoryProjectionStatus,
  eta: InboundEta | undefined,
): Pick<RedoBadgeProps, "color" | "text"> {
  const text = inventoryProjectionStatusLabels[status];
  switch (status) {
    case InventoryProjectionStatus.Covered:
      return { color: "success", text };
    case InventoryProjectionStatus.CoveredByInbound:
      if (!eta) {
        return { color: "gray", text };
      }
      return eta.overdue
        ? { color: "warning", text: `${text} · ${eta.text} · Overdue` }
        : { color: "gray", text: `${text} · ${eta.text}` };
    case InventoryProjectionStatus.Short:
      return { color: "error", text };
    default:
      return assertNever(status);
  }
}

export const InventoryProjectionBadge = memo(function InventoryProjectionBadge({
  status,
  inbound,
  size = "xs",
}: {
  status: InventoryProjectionStatus;
  /** eta is YYYY-MM-DD. */
  inbound?: { eta: string } | null;
  size?: RedoBadgeSize;
}) {
  const { ianaTimezone } = useRequiredContext(TeamContext);
  const eta = inbound
    ? getInboundEta(inbound.eta, {
        now: Temporal.Now.instant(),
        timeZone: ianaTimezone,
      })
    : undefined;
  return <RedoBadge size={size} {...getBadgeProps(status, eta)} />;
});
